/**
 * CSV round-trip for catalogue products.
 *
 * Export writes exactly the columns import reads, so the normal workflow is
 * export, edit in Excel or Google Sheets, import back. A row whose `id` cell is
 * filled updates that product; a blank `id` creates a new one.
 *
 * CSV rather than .xlsx because both Excel and Google Sheets read and write it
 * natively, which keeps this dependency-free.
 */

export type ProductCsvMode = 'plain' | 'categorised';

/** Column order as written on export. `category` only applies with categories. */
const COLUMNS: Record<ProductCsvMode, string[]> = {
  plain: ['id', 'name', 'description', 'price', 'features', 'image', 'order', 'active'],
  categorised: ['id', 'category', 'name', 'description', 'price', 'features', 'image', 'order', 'active'],
};

export function productCsvColumns(mode: ProductCsvMode): string[] {
  return COLUMNS[mode];
}

/**
 * Accepted spellings for each column, so a sheet that has been renamed or
 * reordered by hand still imports. Keys are headers with case, spaces and
 * punctuation stripped.
 */
const HEADER_ALIASES: Record<string, string> = {
  id: 'id',
  productid: 'id',
  name: 'name',
  productname: 'name',
  product: 'name',
  title: 'name',
  description: 'description',
  desc: 'description',
  details: 'description',
  price: 'price',
  rate: 'price',
  mrp: 'price',
  features: 'features',
  image: 'image',
  imageurl: 'image',
  photo: 'image',
  category: 'category',
  categoryname: 'category',
  order: 'order',
  sortorder: 'order',
  position: 'order',
  active: 'active',
  isactive: 'active',
  visible: 'active',
};

/** Text fields that are copied through verbatim in both directions. */
const TEXT_FIELDS = ['name', 'description', 'price', 'features', 'image'] as const;

export type ProductTextField = (typeof TEXT_FIELDS)[number];

export interface ParsedProductRow {
  /** Line number in the file, so an error can point the user at a row. */
  rowNumber: number;
  /** Empty means "create"; otherwise the product to update. */
  id: string;
  /** Category name as typed in the sheet. Empty for plain catalogues. */
  category: string;
  text: Record<ProductTextField, string>;
  /** Absent when the cell was blank, which leaves the value as it is. */
  order?: number;
  isActive?: boolean;
}

export interface ParsedProductCsv {
  rows: ParsedProductRow[];
  /** Problems that stopped a row from being imported, phrased for the user. */
  errors: string[];
}

function normaliseHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * RFC 4180 reader: quoted fields may contain commas, quotes (doubled) and
 * newlines. Line endings are normalised first so a file saved on Windows or by
 * an older Mac Excel parses the same way.
 */
function splitCsv(input: string): string[][] {
  const text = input.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Excel leaves trailing empty lines behind; they are not products.
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
}

function escapeCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function toCsv(columns: string[], rows: Record<string, string>[]): string {
  const lines = [columns.join(',')];
  for (const row of rows) {
    lines.push(columns.map((column) => escapeCell(row[column] ?? '')).join(','));
  }
  return lines.join('\r\n');
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

/** Category name of a product, whether the API populated `categoryId` or not. */
function categoryNameOf(product: any): string {
  if (product?.category) return String(product.category);
  const populated = product?.categoryId;
  if (populated && typeof populated === 'object' && populated.categoryName) {
    return String(populated.categoryName);
  }
  return '';
}

export function productsToCsv(products: any[], mode: ProductCsvMode): string {
  const columns = productCsvColumns(mode);
  const rows = products.map((product) => {
    const row: Record<string, string> = {
      id: cell(product?._id),
      name: cell(product?.name),
      description: cell(product?.description),
      price: cell(product?.price),
      features: cell(product?.features),
      image: cell(product?.image),
      order: cell(product?.order),
      active: product?.isActive === false ? 'FALSE' : 'TRUE',
    };
    if (mode === 'categorised') row.category = categoryNameOf(product);
    return row;
  });
  return toCsv(columns, rows);
}

/** 24-character hex, the shape of a MongoDB id. */
function looksLikeId(value: string): boolean {
  return /^[a-f\d]{24}$/i.test(value);
}

function parseBoolean(value: string): boolean | undefined | 'invalid' {
  const text = value.trim().toLowerCase();
  if (text === '') return undefined;
  if (['true', 'yes', 'y', '1', 'active', 'visible'].includes(text)) return true;
  if (['false', 'no', 'n', '0', 'inactive', 'hidden'].includes(text)) return false;
  return 'invalid';
}

export function parseProductCsv(text: string, mode: ProductCsvMode): ParsedProductCsv {
  const table = splitCsv(text);
  if (table.length === 0) {
    return { rows: [], errors: ['That file is empty.'] };
  }

  const [headerCells, ...bodyRows] = table;
  const columnOf: Record<string, number> = {};
  headerCells.forEach((header, index) => {
    const key = HEADER_ALIASES[normaliseHeader(header)];
    // First occurrence wins, so a stray duplicate column is ignored.
    if (key && columnOf[key] === undefined) columnOf[key] = index;
  });

  if (columnOf.name === undefined) {
    return {
      rows: [],
      errors: [
        'No "name" column found. Export your products first and edit that file, so the columns match.',
      ],
    };
  }

  const rows: ParsedProductRow[] = [];
  const errors: string[] = [];
  const seenIds = new Set<string>();

  bodyRows.forEach((cells, index) => {
    // +2: one for the header row, one to count from 1 like a spreadsheet does.
    const rowNumber = index + 2;
    const read = (key: string) => {
      const at = columnOf[key];
      return at === undefined ? '' : (cells[at] ?? '').trim();
    };

    const name = read('name');
    if (!name) {
      errors.push(`Row ${rowNumber}: name is required.`);
      return;
    }

    const id = read('id');
    if (id && !looksLikeId(id)) {
      errors.push(`Row ${rowNumber}: "${id}" is not a valid id. Leave it blank to add a new product.`);
      return;
    }
    if (id && seenIds.has(id)) {
      errors.push(`Row ${rowNumber}: id ${id} appears more than once.`);
      return;
    }
    if (id) seenIds.add(id);

    const category = mode === 'categorised' ? read('category') : '';
    if (mode === 'categorised' && !category) {
      errors.push(`Row ${rowNumber}: category is required.`);
      return;
    }

    const orderText = read('order');
    let order: number | undefined;
    if (orderText !== '') {
      const parsed = Number(orderText);
      if (!Number.isFinite(parsed)) {
        errors.push(`Row ${rowNumber}: order must be a number.`);
        return;
      }
      order = parsed;
    }

    const isActive = parseBoolean(read('active'));
    if (isActive === 'invalid') {
      errors.push(`Row ${rowNumber}: active must be TRUE or FALSE.`);
      return;
    }

    const textValues = {} as Record<ProductTextField, string>;
    for (const field of TEXT_FIELDS) textValues[field] = read(field);
    textValues.name = name;

    rows.push({ rowNumber, id, category, text: textValues, order, isActive });
  });

  return { rows, errors };
}

/**
 * Fields to send for a row. Only columns present in the file are included, so
 * dropping a column from the sheet leaves that field untouched rather than
 * clearing it.
 */
export function productPayload(row: ParsedProductRow, presentColumns: Set<string>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of TEXT_FIELDS) {
    if (presentColumns.has(field)) payload[field] = row.text[field];
  }
  if (row.order !== undefined) payload.order = row.order;
  if (row.isActive !== undefined) payload.isActive = row.isActive;
  return payload;
}

/** Which of our known columns the file actually had. */
export function presentColumns(text: string): Set<string> {
  const table = splitCsv(text);
  const header = table[0] || [];
  const present = new Set<string>();
  header.forEach((cellText) => {
    const key = HEADER_ALIASES[normaliseHeader(cellText)];
    if (key) present.add(key);
  });
  return present;
}

/** Excel only reads UTF-8 correctly when the file starts with a BOM. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function productCsvFilename(label: string): string {
  const slug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'products';
  return `${slug}-products-${new Date().toISOString().slice(0, 10)}.csv`;
}
